import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { fetchCreditLineEvaluation } from 'lib/api/decisionEngine';
import AlertBanner from 'lib/designSystem/AlertBanner';
import PageHeader from 'lib/designSystem/PageHeader';
import React from 'react';
import { useDispatch } from 'react-redux';

import EvaluationInfoCard from './Components/EvaluationInfoCard';
import NodesSection from './Components/NodesSection';
import { isNodeWaiting } from './Components/types';

interface EvaluationDetailsProps {
  evaluationId: number | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const LoadingSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Skeleton variant="rounded" height={90} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} variant="rounded" height={44} />
      ))}
    </Box>
  </Box>
);

const EvaluationDetails: React.FC<EvaluationDetailsProps> = ({
  evaluationId,
  open,
  setOpen,
}) => {
  const dispatch = useDispatch();
  const { data: evaluation, isPending } = fetchCreditLineEvaluation(
    dispatch,
    evaluationId,
  );

  const nodes = evaluation?.result ?? [];
  const hasWaiting = nodes.some((node) => isNodeWaiting(node));
  const state = evaluation?.state;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <PageHeader.TitleOnly
          variant="dialogHeader"
          iconType="insights"
          title="Detalle de evaluación"
          subtitle={
            evaluation
              ? `${evaluation.businessName} · ${evaluation.businessIdentifier}`
              : undefined
          }
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ml: 2,
            flexShrink: 0,
          }}
        >
          <IconButton onClick={() => setOpen(false)} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {isPending ? (
          <LoadingSkeleton />
        ) : !evaluation ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography color="text.secondary">
              No se encontró la evaluación.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <EvaluationInfoCard
              evaluation={evaluation}
              hasWaiting={hasWaiting}
              state={state}
            />
            {state === 'Fallido' && (
              <AlertBanner
                severity="error"
                title="La evaluación falló."
                message="Hubo un problema al procesar la evaluación. Se hara un reintento automático en unos minutos."
              />
            )}
            <NodesSection nodes={nodes} hasWaiting={hasWaiting} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EvaluationDetails;
